import type { ComponentType } from 'react';
import type { RouteComponentProps, StaticContext } from 'react-router';

import { NewsFormPage } from 'NewsPackage/FormModule/components/NewsFormPage';
import { FeedbackPage } from 'FeedbackPackage/MainModule/components/FeedbackPage';
import { NewsPage } from 'NewsPackage/MainModule/components/NewsPage';
import { SystemInoperabilityPage } from 'NewsPackage/MainModule/components/SystemInoperabilityPage';
import { KnmSearchPage } from 'KnmSearchPackage/MainModule/components/KnmSearchPage';
import { SmevMonitoringPage } from 'SmevMonitoringPackage/MainModule/components/SmevMonitoringPage';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { KnmListPage } from 'KnmListPackage/MainModule/components/KnmListPage';
import { KnmPage } from 'KnmPackage/MainModule/components/KnmPage';
import { KnmPreviewPage } from 'KnmPreviewPackage/MainModule/components/KnmPreviewPage';
import { PreventionPage } from 'PreventionPackage/MainModule/components/PreventionPage';
import { RegistryInfoPage } from 'RegistryPackage/MainModule/components/RegistryInfoPage';
import { ExportPage } from 'ExportPackage/MainModule/components/ExportPage';
import { ImportPage } from 'ImportPackage/MainModule/containers/ImportPage';
import { KnmListByPlanPage } from 'KnmListByPlanPackage/MainModule/components/KnmListByPlanPage';
import { PlanListPage } from 'PlanListPackage/MainModule/components/PlanListPage';
import { PlanPage } from 'PlanPackage/MainModule/components/PlanPage';
import { ReportsPage } from 'ReportsPackage/MainModule/components/ReportsPage';
import { SimilaritiesByPersonPage } from 'SimilaritiesPackage/ByPersonModule/components/SimilaritiesByPersonPage';
import { SimilaritiesPage } from 'SimilaritiesPackage/MainModule/components/SimilaritiesPage';
import { AnalyticsKnmListPage } from 'AnalyticsPackage/KnmListModule/components/AnalyticsKnmListPage';
import { AnalyticsPage } from 'AnalyticsPackage/MainModule/components/AnalyticsPage';
import { DecisionListPage } from 'DecisionListPackage/MainModule/components/DecisionListPage';
import { DecisionPage } from 'DecisionPackage/MainModule/components/DecisionPage';
import { AnalyticsProsecutorOrganizationPage } from 'AnalyticsPackage/MainModule/components/AnalyticsProsecutorOrganizationPage';
import { AgreementListPage } from 'AgreementListPackage/MainModule/components/AgreementListPage';

import {
  ROUTE_ADMIN_ADD_NEWS,
  ROUTE_ANALYTICS,
  ROUTE_ANALYTICS_KNM,
  ROUTE_FEEDBACK,
  ROUTE_SYSTEM_INOPERABILITY,
  ROUTE_KNM_ID,
  ROUTE_KNM_LIST,
  ROUTE_KNM_LIST_BY_PLAN,
  ROUTE_KNM_PREVIEW,
  ROUTE_NEWS,
  ROUTE_PLAN,
  ROUTE_PM_ID,
  ROUTE_PREVENTION,
  ROUTE_REGISTRY_EGRIP,
  ROUTE_REGISTRY_EGRUL,
  ROUTE_REGISTRY_MSP,
  ROUTE_REGISTRY_OEZ,
  ROUTE_REGISTRY_SPV,
  ROUTE_REGISTRY_TOSER,
  ROUTE_REPORTS,
  ROUTE_SEARCH,
  ROUTE_SIMILARITIES,
  ROUTE_SMEV_MONITORING,
  ROUTE_TEMPLATES,
  ROUTE_TEMPLATES_EXPORT,
  ROUTE_TEMPLATES_IMPORT,
  ROUTE_DECISIONS,
  ROUTE_DECISION_ID,
  ROUTE_PASSPORT_MASS_CANCELLATION_NEW_GROUP,
  ROUTE_PASSPORT_MASS_CANCELLATION_GROUP,
  ROUTE_PASSPORT_MASS_DELETION_NEW_GROUP,
  ROUTE_PASSPORT_MASS_DELETION_GROUP,
  ROUTE_ANALYTICS_PROSECTOR,
  ROUTE_ANALYTICS_PROSECTOR_KNM,
  ROUTE_ANALYTICS_KNM_ID,
  ROUTE_ANALYTICS_PM_ID,
  ROUTE_AGREEMENTS,
} from '../routes/routes';
import { PassportMassCancellationCreateGroupPage } from '../../../PersonalAccountPackage/PassportMassCancellationModule/components/PassportMassCancellationCreateGroupPage';
import { PassportMassCancellationGroupPage } from '../../../PersonalAccountPackage/PassportMassCancellationModule/components/PassportMassCancellationGroupPage';
import { PassportMassDeletionCreateGroupPage } from '../../../PersonalAccountPackage/PassportMassDeletionModule/components/PassportMassDeletionCreateGroupPage';
import { PassportMassDeletionGroupPage } from '../../../PersonalAccountPackage/PassportMassDeletionModule/components/PassportMassDeletionGroupPage';

interface IRoute {
  path: string;
  element: ComponentType<unknown> | ComponentType<RouteComponentProps<{}, StaticContext, unknown>> | undefined;
  checkOrganizations?: boolean;
  exact?: boolean;
}

export const getRoutes = (isInternal: boolean, isErknm: boolean, sysName?: UserRoleEnum): IRoute[] | null => {
  const baseRoutes = [
    { path: ROUTE_ANALYTICS_KNM_ID, element: KnmPage, checkOrganizations: true },
    { path: ROUTE_ANALYTICS_PM_ID, element: KnmPage, checkOrganizations: true },
    { path: ROUTE_KNM_ID, element: KnmPage, checkOrganizations: true },
    { path: ROUTE_PM_ID, element: KnmPage, checkOrganizations: true },
    { path: ROUTE_REGISTRY_TOSER, element: RegistryInfoPage },
    { path: ROUTE_REGISTRY_SPV, element: RegistryInfoPage },
    { path: ROUTE_REGISTRY_OEZ, element: RegistryInfoPage },
    { path: ROUTE_REGISTRY_MSP, element: RegistryInfoPage },
    { path: ROUTE_REGISTRY_EGRUL, element: RegistryInfoPage },
    { path: ROUTE_REGISTRY_EGRIP, element: RegistryInfoPage },
    { path: ROUTE_TEMPLATES, element: PlanListPage, checkOrganizations: true, exact: true },
    {
      path: ROUTE_KNM_LIST_BY_PLAN,
      element: KnmListByPlanPage,
      checkOrganizations: true,
      exact: true,
    },
    { path: ROUTE_PLAN, element: PlanPage, checkOrganizations: true, exact: true },
    {
      path: ROUTE_TEMPLATES_IMPORT,
      element: ImportPage,
      checkOrganizations: true,
      exact: true,
    },
    {
      path: ROUTE_TEMPLATES_EXPORT,
      element: ExportPage,
      checkOrganizations: true,
      exact: true,
    },
    {
      path: ROUTE_SIMILARITIES,
      element: SimilaritiesPage,
      checkOrganizations: true,
      exact: true,
    },
    {
      path: `${ROUTE_SIMILARITIES}/:id`,
      element: SimilaritiesByPersonPage,
      checkOrganizations: true,
      exact: true,
    },
    { path: ROUTE_KNM_LIST, element: KnmListPage, checkOrganizations: true },
    { path: ROUTE_PREVENTION, element: PreventionPage, checkOrganizations: true, exact: true },
    { path: ROUTE_DECISIONS, element: DecisionListPage, exact: true, checkOrganizations: true },
    { path: ROUTE_AGREEMENTS, element: AgreementListPage, exact: true, checkOrganizations: true },
    { path: ROUTE_DECISION_ID, element: DecisionPage, exact: true, checkOrganizations: true },
    { path: ROUTE_SEARCH, element: KnmSearchPage, exact: true },
    { path: ROUTE_KNM_PREVIEW, element: KnmPreviewPage, exact: true },
    { path: ROUTE_NEWS, element: NewsPage, exact: true },
    { path: ROUTE_SYSTEM_INOPERABILITY, element: SystemInoperabilityPage, exact: true },
    { path: ROUTE_REPORTS, element: ReportsPage, checkOrganizations: true, exact: true },
    { path: ROUTE_FEEDBACK, element: FeedbackPage, exact: true },
    {
      path: ROUTE_PASSPORT_MASS_CANCELLATION_NEW_GROUP,
      element: PassportMassCancellationCreateGroupPage,
      checkOrganizations: true,
      exact: true,
    },
    {
      path: ROUTE_PASSPORT_MASS_CANCELLATION_GROUP,
      element: PassportMassCancellationGroupPage,
      checkOrganizations: true,
      exact: true,
    },
    {
      path: ROUTE_PASSPORT_MASS_DELETION_NEW_GROUP,
      element: PassportMassDeletionCreateGroupPage,
      checkOrganizations: true,
      exact: true,
    },
    {
      path: ROUTE_PASSPORT_MASS_DELETION_GROUP,
      element: PassportMassDeletionGroupPage,
      checkOrganizations: true,
      exact: true,
    },
  ];

  switch (sysName) {
    case UserRoleEnum.ADMIN:
      return [
        { path: ROUTE_ADMIN_ADD_NEWS, element: NewsFormPage, exact: true },
        { path: `${ROUTE_NEWS}/:newsId`, element: NewsFormPage, exact: true },
        { path: ROUTE_SEARCH, element: KnmSearchPage, exact: true },
        { path: ROUTE_NEWS, element: NewsPage, exact: true },
        { path: ROUTE_SYSTEM_INOPERABILITY, element: SystemInoperabilityPage, exact: true },
        { path: ROUTE_FEEDBACK, element: FeedbackPage, exact: true },
        { path: ROUTE_SMEV_MONITORING, element: SmevMonitoringPage },
      ];
    case UserRoleEnum.OMBUDSMAN:
      return [
        { path: ROUTE_KNM_ID, element: KnmPage, checkOrganizations: true },
        { path: ROUTE_PM_ID, element: KnmPage, checkOrganizations: true },
        { path: ROUTE_REGISTRY_TOSER, element: RegistryInfoPage },
        { path: ROUTE_REGISTRY_SPV, element: RegistryInfoPage },
        { path: ROUTE_REGISTRY_OEZ, element: RegistryInfoPage },
        { path: ROUTE_REGISTRY_MSP, element: RegistryInfoPage },
        { path: ROUTE_REGISTRY_EGRUL, element: RegistryInfoPage },
        { path: ROUTE_REGISTRY_EGRIP, element: RegistryInfoPage },
        { path: ROUTE_KNM_LIST, element: KnmListPage, checkOrganizations: true },
        {
          path: ROUTE_PREVENTION,
          element: PreventionPage,
          checkOrganizations: true,
          exact: true,
        },
        { path: ROUTE_SEARCH, element: KnmSearchPage, exact: true },
        { path: ROUTE_KNM_PREVIEW, element: KnmPreviewPage, exact: true },
        { path: ROUTE_NEWS, element: NewsPage, exact: true },
        { path: ROUTE_SYSTEM_INOPERABILITY, element: SystemInoperabilityPage, exact: true },
        { path: ROUTE_FEEDBACK, element: FeedbackPage, exact: true },
        {
          path: ROUTE_TEMPLATES_EXPORT,
          element: ExportPage,
          checkOrganizations: true,
          exact: true,
        },
        { path: ROUTE_DECISIONS, element: DecisionListPage, exact: true },
        { path: ROUTE_AGREEMENTS, element: AgreementListPage, exact: true, checkOrganizations: true },
        { path: ROUTE_DECISION_ID, element: DecisionPage, exact: true },
      ];
    case UserRoleEnum.SUPERVISOR: {
      const supervisorRoutes = [
        ...baseRoutes,
        {
          path: ROUTE_ANALYTICS_KNM,
          element: AnalyticsKnmListPage,
          checkOrganizations: true,
          exact: true,
        },
        { path: ROUTE_ANALYTICS, element: AnalyticsPage, checkOrganizations: true, exact: true },
      ];

      if (isErknm && !isInternal) {
        return supervisorRoutes.filter(item => item.path !== ROUTE_TEMPLATES_IMPORT);
      }

      return baseRoutes;
    }
    case UserRoleEnum.SUPERIOR_PROSECUTOR:
    case UserRoleEnum.PROSECUTOR:
      return [
        ...baseRoutes,
        {
          path: ROUTE_ANALYTICS_KNM,
          element: AnalyticsKnmListPage,
          checkOrganizations: true,
          exact: true,
        },
        { path: ROUTE_ANALYTICS, element: AnalyticsPage, checkOrganizations: true, exact: true },
        {
          path: ROUTE_ANALYTICS_PROSECTOR,
          element: AnalyticsProsecutorOrganizationPage,
          checkOrganizations: true,
          exact: true,
        },
        {
          path: ROUTE_ANALYTICS_PROSECTOR_KNM,
          element: AnalyticsKnmListPage,
          checkOrganizations: true,
          exact: true,
        },
      ];
    default:
      return null;
  }
};
